import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { toast } from '../../hooks/use-toast';
import { errorMessage } from '../errors';
import { digestApi } from '../digestApi';
import { useAuth } from '../AuthContext';
import PickSelect from '../shared/PickSelect';
import BellPrefsSection from './BellPrefsSection';
import EmailPrefsSection from './EmailPrefsSection';
import TextingHoursSection from './TextingHoursSection';

// One switch per digest section/filter. `on` is the saved value the switch shows.
const SECTIONS = [
  { key: 'stalled', label: 'Stalled deals' },
  { key: 'hot_leads', label: 'Hot leads' },
  { key: 'rate_locks', label: 'Expiring rate locks' },
  { key: 'conditions', label: 'Outstanding conditions' },
  { key: 'include_closed_wins', label: 'Closed wins' },
];
const WINDOWS = [7, 14, 30].map((d) => ({ value: String(d), label: `${d} days` }));

function DigestSection() {
  const { user } = useAuth();
  // The Monday email goes only to the workspace's owner and admin seats (users.role == "admin").
  const isAdmin = user?.role === 'admin';
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isAdmin) return;
    digestApi.getDigestPrefs()
      .then(setPrefs)
      .catch((e) => toast({ title: 'Could not load notification settings', description: errorMessage(e) }));
  }, [isAdmin]);

  // POST replaces the whole subdocument: always send the full merged object.
  const save = async (patch, title) => {
    setSaving(true);
    try {
      const saved = await digestApi.setDigestPrefs({ ...prefs, ...patch });
      setPrefs(saved);
      toast({ title });
    } catch (e) {
      toast({ title: 'Could not save notification settings', description: errorMessage(e) });
    } finally {
      setSaving(false);
    }
  };

  const toggle = (checked) => save({ disabled: !checked }, checked ? 'Weekly digest turned on' : 'Weekly digest turned off');
  const setSection = (key, label, checked) => save({ [key]: checked }, `${label} ${checked ? 'added to' : 'removed from'} the digest`);

  if (!isAdmin) {
    return (
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 max-w-lg space-y-1" data-testid="notifications-admin-only">
        <h2 className="font-serif text-lg font-bold" style={{ color: '#070B5F' }}>Weekly digest email</h2>
        <p className="text-sm text-slate-500">The Monday digest goes to workspace owners and admins only, so there is nothing to set for your seat.</p>
      </div>
    );
  }

  if (!prefs) {
    return (
      <div className="p-8 flex items-center justify-center text-slate-400" data-testid="notifications-loading">
        <Loader2 className="animate-spin mr-2" size={18} /> Loading...
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 max-w-lg space-y-3" data-testid="notifications-tab">
      <div>
        <h2 className="font-serif text-lg font-bold" style={{ color: '#070B5F' }}>Weekly digest email</h2>
        <p className="text-sm text-slate-500 mt-1">
          A Monday 8 AM summary of stalled deals, hot leads, expiring rate locks and closed wins. It goes to workspace owners and admins only, and each of them sets their own copy here.
        </p>
      </div>
      <div className="flex items-center gap-3">
        <Switch id="digest-enabled" checked={!prefs.disabled} onCheckedChange={toggle} disabled={saving}
          data-testid="notifications-digest-switch" />
        <Label htmlFor="digest-enabled" className="text-sm">Email me the weekly digest</Label>
      </div>
      <div className={`border-t border-slate-100 pt-3 space-y-3 ${prefs.disabled ? 'opacity-50' : ''}`} data-testid="notifications-sections">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Include in my digest</div>
        {SECTIONS.map(({ key, label }) => (
          <div key={key} className="flex items-center gap-3">
            <Switch id={`digest-${key}`} checked={!!prefs[key]} onCheckedChange={(c) => setSection(key, label, c)}
              disabled={saving || prefs.disabled} data-testid={`notifications-${key}`} />
            <Label htmlFor={`digest-${key}`} className="text-sm">{label}</Label>
          </div>
        ))}
        <div className="flex items-center gap-3">
          <Switch id="digest-only-mine" checked={!!prefs.only_my_leads} onCheckedChange={(c) => save({ only_my_leads: c }, c ? 'Digest limited to your own deals' : 'Digest covers the whole workspace')}
            disabled={saving || prefs.disabled} data-testid="notifications-only_my_leads" />
          <Label htmlFor="digest-only-mine" className="text-sm">Only deals where I am the loan officer</Label>
        </div>
        <PickSelect label="Look-back / look-ahead window" value={String(prefs.window_days)} options={WINDOWS}
          onChange={(v) => save({ window_days: Number(v) }, `Digest window set to ${v} days`)} testId="notifications-window" />
      </div>
    </div>
  );
}

export default function NotificationsTab() {
  return (
    <div className="space-y-4" data-testid="notifications-page">
      <BellPrefsSection />
      <EmailPrefsSection />
      <TextingHoursSection />
      <DigestSection />
    </div>
  );
}
